import { makeAutoObservable, runInAction } from 'mobx';
import type {
  DriverLocationData,
  OrderDispatchedData,
  OrderEvent,
  OrderEventData,
  OrderStatusUpdateData,
  RealtimeEvent,
} from '@vn-propane/shared/client';

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'error';

export class RealtimeStore {
  private ws: WebSocket | null = null;
  private reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private connectionTimeout: ReturnType<typeof setTimeout> | null = null;
  private lastPongTime: number = 0;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10; // Increased for high latency
  private reconnectDelay = 2000; // Start higher for slow networks
  private pingIntervalMs = 15000; // Send ping every 15 seconds
  private pongTimeoutMs = 30000; // Consider dead if no pong in 30 seconds
  private connectionTimeoutMs = 20000; // Connection must complete in 20 seconds

  private currentOrgId: string | null = null;
  private currentApiBaseUrl: string | null = null;

  connectionStatus: ConnectionStatus = 'disconnected';
  lastError: string | null = null;

  // Recent events for display
  recentOrderEvents: OrderEvent[] = [];

  // Driver locations - map of driver_id to latest location
  driverLocations: Map<string, DriverLocationData> = new Map();

  // Callbacks for handling events
  private onOrderCreatedCallbacks: ((data: OrderEventData) => void)[] = [];
  private onOrderDispatchedCallbacks: ((data: OrderDispatchedData) => void)[] = [];
  private onOrderStatusUpdatedCallbacks: ((data: OrderStatusUpdateData) => void)[] = [];
  private onDriverLocationCallbacks: ((data: DriverLocationData) => void)[] = [];

  constructor() {
    makeAutoObservable(this);
  }

  connect(orgId: string, apiBaseUrl: string) {
    if (this.ws?.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }

    // Clean up any existing connection
    this.cleanup();

    this.currentOrgId = orgId;
    this.currentApiBaseUrl = apiBaseUrl;
    this.connectionStatus = 'connecting';
    this.lastError = null;

    // Construct WebSocket URL from API base URL
    const wsProtocol = apiBaseUrl.startsWith('https') ? 'wss' : 'ws';
    const host = apiBaseUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
    const wsUrl = `${wsProtocol}://${host}/realtime/gas_admin/ws?org_id=${encodeURIComponent(orgId)}`;

    console.log('Connecting to WebSocket:', wsUrl);

    try {
      this.ws = new WebSocket(wsUrl);

      // Set connection timeout
      this.connectionTimeout = setTimeout(() => {
        if (this.ws?.readyState !== WebSocket.OPEN) {
          console.log('WebSocket connection timeout');
          this.ws?.close();
          runInAction(() => {
            this.connectionStatus = 'error';
            this.lastError = 'Connection timeout';
          });
          this.scheduleReconnect();
        }
      }, this.connectionTimeoutMs);

      this.ws.onopen = () => {
        // Clear connection timeout
        if (this.connectionTimeout) {
          clearTimeout(this.connectionTimeout);
          this.connectionTimeout = null;
        }

        runInAction(() => {
          this.connectionStatus = 'connected';
          this.reconnectAttempts = 0;
          this.lastPongTime = Date.now();
          console.log('WebSocket connected');
        });

        // Start heartbeat
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data) as RealtimeEvent;
          this.handleEvent(data);
        } catch (e) {
          console.error('Failed to parse WebSocket message:', e);
        }
      };

      this.ws.onclose = (event) => {
        this.stopHeartbeat();

        runInAction(() => {
          this.connectionStatus = 'disconnected';
          console.log('WebSocket closed:', event.code, event.reason);
        });

        // Attempt to reconnect if we still have connection params
        if (this.currentOrgId && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = (error) => {
        runInAction(() => {
          this.connectionStatus = 'error';
          this.lastError = 'WebSocket connection error';
          console.error('WebSocket error:', error);
        });
      };
    } catch (e) {
      runInAction(() => {
        this.connectionStatus = 'error';
        this.lastError = 'Failed to create WebSocket connection';
      });
    }
  }

  private scheduleReconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
    }

    if (!this.currentOrgId || !this.currentApiBaseUrl) {
      return;
    }

    this.reconnectAttempts++;
    // Exponential backoff with max delay of 30 seconds
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000);

    console.log(`Scheduling reconnect attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts} in ${delay}ms`);

    this.reconnectTimeout = setTimeout(() => {
      if (this.currentOrgId && this.currentApiBaseUrl) {
        this.connect(this.currentOrgId, this.currentApiBaseUrl);
      }
    }, delay);
  }

  private startHeartbeat() {
    this.stopHeartbeat();

    this.pingInterval = setInterval(() => {
      // Check if we've received a pong recently
      const timeSinceLastPong = Date.now() - this.lastPongTime;
      if (timeSinceLastPong > this.pongTimeoutMs) {
        console.log(`No pong received in ${timeSinceLastPong}ms, connection may be dead`);
        this.ws?.close();
        return;
      }

      // Send ping
      this.sendPing();
    }, this.pingIntervalMs);
  }

  private stopHeartbeat() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  private sendPing() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'ping' }));
    }
  }

  private cleanup() {
    this.stopHeartbeat();

    if (this.connectionTimeout) {
      clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }

    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  disconnect() {
    this.cleanup();
    this.currentOrgId = null;
    this.currentApiBaseUrl = null;
    this.connectionStatus = 'disconnected';
    this.reconnectAttempts = 0;
  }

  private handleEvent(event: RealtimeEvent) {
    switch (event.type) {
      case 'connected':
        console.log('Realtime connected:', event.payload.message);
        break;

      case 'order':
        this.handleOrderEvent(event.payload);
        break;

      case 'driver_location':
        this.handleDriverLocation(event.payload);
        break;

      case 'ping':
        // Send pong response
        this.sendPong();
        break;

      case 'pong':
        // Update last pong time for heartbeat check
        this.lastPongTime = Date.now();
        break;
    }
  }

  private handleDriverLocation(location: DriverLocationData) {
    runInAction(() => {
      this.driverLocations.set(location.driver_id, location);
    });
    this.onDriverLocationCallbacks.forEach(cb => cb(location));
  }

  private handleOrderEvent(orderEvent: OrderEvent) {
    runInAction(() => {
      // Add to recent events (keep last 50)
      this.recentOrderEvents = [orderEvent, ...this.recentOrderEvents].slice(0, 50);
    });

    // Notify callbacks
    switch (orderEvent.event) {
      case 'order_created':
        this.onOrderCreatedCallbacks.forEach(cb => cb(orderEvent));
        break;

      case 'order_dispatched':
        this.onOrderDispatchedCallbacks.forEach(cb => cb(orderEvent));
        break;

      case 'order_status_updated':
        this.onOrderStatusUpdatedCallbacks.forEach(cb => cb(orderEvent));
        break;
    }
  }

  private sendPong() {
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'pong' }));
    }
  }

  // Subscribe to events
  onOrderCreated(callback: (data: OrderEventData) => void): () => void {
    this.onOrderCreatedCallbacks.push(callback);
    return () => {
      this.onOrderCreatedCallbacks = this.onOrderCreatedCallbacks.filter(cb => cb !== callback);
    };
  }

  onOrderDispatched(callback: (data: OrderDispatchedData) => void): () => void {
    this.onOrderDispatchedCallbacks.push(callback);
    return () => {
      this.onOrderDispatchedCallbacks = this.onOrderDispatchedCallbacks.filter(cb => cb !== callback);
    };
  }

  onOrderStatusUpdated(callback: (data: OrderStatusUpdateData) => void): () => void {
    this.onOrderStatusUpdatedCallbacks.push(callback);
    return () => {
      this.onOrderStatusUpdatedCallbacks = this.onOrderStatusUpdatedCallbacks.filter(cb => cb !== callback);
    };
  }

  onDriverLocation(callback: (data: DriverLocationData) => void): () => void {
    this.onDriverLocationCallbacks.push(callback);
    return () => {
      this.onDriverLocationCallbacks = this.onDriverLocationCallbacks.filter(cb => cb !== callback);
    };
  }

  // Get all driver locations as an array
  getDriverLocationsArray(): DriverLocationData[] {
    return Array.from(this.driverLocations.values());
  }

  // Get a specific driver's location
  getDriverLocation(driverId: string): DriverLocationData | undefined {
    return this.driverLocations.get(driverId);
  }

  // Clear all events
  clearEvents() {
    this.recentOrderEvents = [];
  }

  // Clear driver locations
  clearDriverLocations() {
    this.driverLocations.clear();
  }
}

// Singleton instance
export const realtimeStore = new RealtimeStore();
